import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  ListItemText,
  OutlinedInput,
  Checkbox,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { CloseOutlined } from "@mui/icons-material";
import api from "API/api";
import MDBox from "components/MDBox";
import colors from "assets/theme/base/colors";
import * as Yup from "yup";
import { toast } from "sonner";

const AddUsersModal = ({
  addUserOpen,
  setAddUserOpen,
  currentUser = null,
  getAllUsers,
}) => {
  const { white, gradients } = colors;
  const isEdit = Boolean(currentUser);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";
  const [siteData, setSiteData] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(
    isAdmin ? "" : user.site_id
  );
  const [loading, setLoading] = useState(false);

  const initialValues = {
    username: currentUser?.username || "",
    firstname: currentUser?.firstname || "",
    lastname: currentUser?.lastname || "",
    email: currentUser?.email || "",
    phone: currentUser?.phone || "",
    role: currentUser?.role || "",
    password: "",
    con_password: "",
    site_id: isEdit
      ? currentUser?.site_data?.map((site) => Number(site.site_id)) || []
      : selectedLocation
      ? [selectedLocation]
      : [],
  };
  const validationSchema = isEdit
    ? null
    : Yup.object().shape({
        username: Yup.string()
          .required("User name is required")
          .min(2, "User name must be at least 2 characters long"),
        firstname: Yup.string()
          .required("First name is required")
          .min(2, "First name must be at least 2 characters long"),
        lastname: Yup.string()
          .required("Last name is required")
          .min(2, "Last name must be at least 2 characters long"),
        email: Yup.string()
          .email("Invalid email address")
          .required("Email is required"),
        phone: Yup.string()
          .matches(/^[0-9]+$/, "Phone number must be only digits")
          .min(10, "Phone number must be at least 10 digits long")
          .required("Phone number is required"),
        password: Yup.string()
          .required("Password is required")
          .min(6, "Password must be at least 6 characters long"),
        con_password: Yup.string()
          .oneOf([Yup.ref("password"), null], "Passwords must match")
          .required("Confirm password is required"),
        role: Yup.string().required("Please Select a role"),
        // site_id: Yup.array().min(1, "Please select at least one site"),
      });

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get("/cv/siteapi");
        setSiteData(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedLocation(response.data.data[0].site_id);
        }
      } catch (error) {
        alert("Error fetching sites:", error);
      }
    };

    fetchSites();
  }, []);

  const handleSubmit = async (values, actions) => {
    const formData = new FormData();
    // const siteIdToUse = isAdmin ? values.site_id : user.site_id;
    formData.append("sites", JSON.stringify(values.site_id));
    formData.append("role", values?.role);

    if (isEdit) {
      Object.keys(values).forEach((key) => {
        if (values[key] !== initialValues[key] && key !== "site_id") {
          formData.append(key, values[key]);
        }
      });
      formData.append("uid", currentUser?.uid);
    } else {
      Object.keys(values).forEach((key) => {
        if (key !== "site_id") {
          formData.append(key, values[key]);
        }
      });
    }

    try {
      setLoading(true);
      const response = isEdit
        ? await api.put(`/userapi`, formData)
        : await api.post(`/userapi`, formData);
      toast.success(response.data.message);
      setAddUserOpen(false);
      actions.resetForm();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      actions.setSubmitting(false);
      getAllUsers();
      setLoading(false);
    }
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "14px",
    outline: "none !important",
  };

  return (
    <Modal open={addUserOpen} onClose={() => setAddUserOpen(false)}>
      <Box sx={style} className="add-user-modal">
        {(!isEdit || (isEdit && currentUser)) && (
          <Formik
            initialValues={initialValues}
            validationSchema={!isEdit ? validationSchema : undefined}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              isSubmitting,
              dirty,
            }) => (
              <Form>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: gradients.info.main,
                    borderTopLeftRadius: "14px",
                    borderTopRightRadius: "14px",
                  }}
                >
                  <Box
                    sx={{
                      padding: "15px",
                      display: "flex",
                      justifyContent: "space-between",
                      color: white.main,
                    }}
                  >
                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                      {isEdit ? "EDIT USER" : "ADD USER"}
                    </Typography>
                    <CloseOutlined
                      onClick={() => setAddUserOpen(false)}
                      sx={{ cursor: "pointer", fontSize: "30px", mt: "5px" }}
                    />
                  </Box>
                </Box>
                <Box sx={{ padding: "15px" }}>
                  <Box mb={2}>
                    <Field
                      as={MDInput}
                      name="username"
                      placeholder="User Name"
                      fullWidth
                      autoComplete="off"
                    />
                    <ErrorMessage
                      name="username"
                      component={MDTypography}
                      variant="caption"
                      color="error"
                    />
                  </Box>
                  <Box mb={2}>
                    <Field
                      as={MDInput}
                      name="firstname"
                      placeholder="First Name"
                      fullWidth
                      autoComplete="off"
                    />
                    <ErrorMessage
                      name="firstname"
                      component={MDTypography}
                      variant="caption"
                      color="error"
                    />
                  </Box>
                  <Box mb={2}>
                    <Field
                      as={MDInput}
                      name="lastname"
                      placeholder="Last Name"
                      fullWidth
                      autoComplete="off"
                    />
                    <ErrorMessage
                      name="lastname"
                      component={MDTypography}
                      variant="caption"
                      color="error"
                    />
                  </Box>
                  <Box mb={2}>
                    <Field
                      as={MDInput}
                      name="email"
                      placeholder="Email"
                      fullWidth
                      autoComplete="off"
                    />
                    <ErrorMessage
                      name="email"
                      component={MDTypography}
                      variant="caption"
                      color="error"
                    />
                  </Box>
                  <Box mb={2}>
                    <Field
                      as={MDInput}
                      name="phone"
                      placeholder="Phone"
                      fullWidth
                      autoComplete="off"
                    />
                    <ErrorMessage
                      name="phone"
                      component={MDTypography}
                      variant="caption"
                      color="error"
                    />
                  </Box>
                  {!isEdit && (
                    <Box mb={2}>
                      <Field
                        as={MDInput}
                        name="password"
                        type="password"
                        placeholder="Password"
                        fullWidth
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name="password"
                        component={MDTypography}
                        variant="caption"
                        color="error"
                      />
                    </Box>
                  )}
                  {!isEdit && (
                    <Box mb={2}>
                      <Field
                        as={MDInput}
                        name="con_password"
                        type="password"
                        placeholder="Confirm Password"
                        fullWidth
                        autoComplete="off"
                      />
                      <ErrorMessage
                        name="con_password"
                        component={MDTypography}
                        variant="caption"
                        color="error"
                      />
                    </Box>
                  )}
                  <Box mb={2}>
                    <Field name="role">
                      {({ field, form }) => (
                        <Select
                          value={field.value || ""}
                          onChange={(e) =>
                            form.setFieldValue("role", e.target.value)
                          }
                          className="react-select"
                          sx={{
                            background: "white",
                            marginRight: "8px",
                            width: "100%",
                          }}
                          displayEmpty
                        >
                          <MenuItem disabled value="">
                            Select Role
                          </MenuItem>
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      )}
                    </Field>
                  </Box>
                  {values.role === "user" && (
                    <Box mb={2}>
                      <Field name="site_id">
                        {({ field, form }) => (
                          <Select
                            multiple
                            value={field.value}
                            onChange={(e) =>
                              form.setFieldValue("site_id", e.target.value)
                            }
                            className="react-select"
                            sx={{
                              background: "white",
                              marginRight: "8px",
                              width: "100%",
                              marginBottom: "28px",
                              color: "gray",
                            }}
                            displayEmpty
                            input={<OutlinedInput />}
                            renderValue={(selected) => {
                              if (!selected || selected.length === 0)
                                return "Select Site";

                              const selectedNames = siteData
                                .filter((site) =>
                                  selected.includes(Number(site.site_id))
                                )
                                .map((site) => site.location);

                              return selectedNames.join(", ");
                            }}
                          >
                            <MenuItem disabled value="">
                              <em>Select Site</em>
                            </MenuItem>
                            {siteData?.map((site) => (
                              <MenuItem
                                key={site.site_id}
                                value={Number(site.site_id)}
                                sx={{ fontSize: "14px !important" }}
                              >
                                <Checkbox
                                  checked={field.value.includes(
                                    Number(site.site_id)
                                  )}
                                  sx={{ marginRight: "8px" }}
                                />
                                <ListItemText
                                  primary={site.location}
                                  primaryTypographyProps={{ fontSize: "14px" }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </Field>
                    </Box>
                  )}

                  <MDBox mt={4} mb={1}>
                    <Button
                      type="submit"
                      sx={{
                        width: "100%",
                        height: "38px",
                        fontSize: "14px",
                        fontWeight: "500",
                        background: !dirty
                          ? "#e0e0e0"
                          : `${gradients.info.state} !important`,
                        color: white.main,
                        border: "none",
                        marginRight: "0",
                        marginBottom: "0",
                        "&:hover": {
                          background: `${gradients.info.state} !important`,
                          color: white.main,
                        },
                      }}
                      disabled={loading || !dirty}
                    >
                      {loading ? (
                        <CircularProgress
                          size={25}
                          sx={{
                            color: "white !important",
                          }}
                        />
                      ) : isSubmitting ? (
                        isEdit ? (
                          "Updating..."
                        ) : (
                          "Adding..."
                        )
                      ) : isEdit ? (
                        "Update"
                      ) : (
                        "Add"
                      )}
                    </Button>
                  </MDBox>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </Modal>
  );
};

export default AddUsersModal;
